import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { initializeGoogleAuth, signInWithGoogle, getStoredUser, storeUser, isRememberLoginEnabled } from '../utils/googleAuth';
import { User } from '../types/alarm';
import EmailAuthModal from '../components/EmailAuthModal';
import IdPasswordFindModal from '../components/IdPasswordFindModal';
import { authService, alarmService } from '../services/firebaseService';
import { classCodeService } from '../services/classCodeService';
import { checkMemberPageAccess } from '../utils/licenseLimits';
import { getOrderedMemberGroups } from '../utils/memberGroupOrder';
import LicenseLimitModal from '../components/LicenseLimitModal';
import PolicyViewModal from '../components/PolicyViewModal';
import PublicLicensePlanModal from '../components/PublicLicensePlanModal';
import { getAvatarInitialsFromEmail, getUiAvatarPlaceholderUrl } from '../utils/uiAvatarPlaceholder';
import { hideScrollbar } from '../styles/hideScrollbar';

interface LoginPageProps {
  onLogin: (user: User) => void;
  themeMode?: 'dark' | 'light';
  onToggleTheme?: () => void;
}

const LoginContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-login-bg);
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 100px 0 0 0;
  min-height: 100vh;
  ${hideScrollbar}
  
  /* 확대되었을 때도 충분한 여백 보장 */
  @media (min-height: 800px) {
    padding: 120px 0 0 0;
  }
  
  /* 매우 작은 화면에서도 충분한 여백 */
  @media (max-height: 600px) {
    padding: 50px 0 0 0;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 40px;
  padding: 0 20px;
  width: 100%;
  box-sizing: border-box;
`;

const LogoSmallAbove = styled.img`
  width: 56px;
  height: auto;
  margin-bottom: 12px;
  object-fit: contain;
`;

const LogoImage = styled.img`
  width: 360px;
  max-width: 85%;
  height: auto;
  margin-bottom: 24px;
`;


const RoleSelectionContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  width: 90%;
  max-width: 450px;
  padding: 0 20px;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const RoleCard = styled.div<{ $isSelected?: boolean }>`
  flex: 1;
  background: var(--color-surface);
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 16px;
  padding: 24px 20px;
  box-shadow: ${props => props.$isSelected ? '0 8px 24px rgba(var(--color-accent-rgb), 0.2)' : '0 4px 12px rgba(0, 0, 0, 0.1)'};
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  transform: ${props => props.$isSelected ? 'translateY(-4px)' : 'none'};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: var(--color-accent-strong);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
`;

const RoleIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto 12px;
  background: rgba(var(--color-accent-rgb), 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 32px;
    height: 32px;
    color: var(--color-accent-text);
  }
`;

const RoleTitle = styled.div`
  color: var(--color-accent-text);
  font-size: 1.3rem;
  font-weight: 700;
`;

const RoleDescription = styled.div`
  color: #666;
  font-size: 0.85rem;
  margin-top: 4px;
  
  /* 다크모드: 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: #777777;
  }
`;

const BackToRoleButton = styled.button`
  background: none;
  border: none;
  color: var(--color-accent-text);
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 16px;
  padding: 8px 0;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:focus {
    outline: none;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const StudentFormGroup = styled.div`
  margin-bottom: 16px;
`;

const StudentTabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const StudentTabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px 12px;
  border: 2px solid ${({ $active }) => ($active ? 'var(--color-accent-strong)' : 'var(--color-border)')};
  background: ${({ $active }) => ($active ? 'var(--color-accent-strong)' : 'var(--color-surface)')};
  color: ${({ $active }) => ($active ? '#ffffff' : 'var(--color-text-primary)')};
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
`;

const StudentFormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 0.9rem;
`;

const StudentFormInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box;
  text-align: center;
  transition: border-color 0.3s ease;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
  
  /* 다크모드: WebKit이 입력 글자색을 덮어쓰는 경우까지 흰색으로 고정 */
  :root:not([data-theme='light']) & {
    background: var(--color-category-input-bg);
    color: #ffffff;
    -webkit-text-fill-color: #ffffff;
    caret-color: #ffffff;
  }
  
  /* 브라우저 자동완성(autofill) 시 배경색을 연한 순수 회색으로 (기본 #E8F0FE 오버라이드) */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px #F0F0F0 inset !important;
    box-shadow: 0 0 0 30px #F0F0F0 inset !important;
    -webkit-text-fill-color: #111111 !important;
  }
  :root:not([data-theme='light']) &:-webkit-autofill,
  :root:not([data-theme='light']) &:-webkit-autofill:hover,
  :root:not([data-theme='light']) &:-webkit-autofill:focus,
  :root:not([data-theme='light']) &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px #2f2f2f inset !important;
    box-shadow: 0 0 0 30px #2f2f2f inset !important;
    -webkit-text-fill-color: #ffffff !important;
    color: #ffffff !important;
  }
  
  &:disabled {
    background: var(--color-panel);
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

/** 멤버·게스트 탭 1단계: 클래스 코드·입장 코드 확인 — 높이는 StudentTabButton과 동일. 완료 시 비활성 스타일은 StudentSubmitButton과 동일 */
const MemberClassVerifyButton = styled.button`
  width: 100%;
  margin-top: 12px;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  white-space: nowrap;
  border: 2px solid var(--color-accent-strong);
  background: var(--color-accent-strong);
  color: #ffffff;
  cursor: pointer;
  transition: background 0.3s ease, color 0.3s ease, border-color 0.3s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    border-color: var(--color-accent);
  }

  &:disabled {
    cursor: not-allowed;
    background: var(--color-save-button-disabled-bg);
    color: var(--color-save-button-disabled-text);
    border-color: var(--color-save-button-disabled-bg);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const VerifiedClassMeta = styled.div`
  margin-top: 12px;
  padding: 12px 14px;
  background: var(--color-panel);
  border-radius: 8px;
  border: 1px solid var(--color-border);
  font-size: 0.95rem;
  color: var(--color-text-primary);
  text-align: center;
`;

const VerifiedClassName = styled.div`
  font-weight: 700;
  color: var(--color-accent-strong);
  margin-bottom: 8px;
  text-align: center;
`;

const GroupSelect = styled.select`
  width: 100%;
  padding: 12px 14px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box;
  text-align: center;
  background: var(--color-surface);
  color: var(--color-text-primary);

  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }

  :root:not([data-theme='light']) & {
    background: var(--color-category-input-bg);
    color: #ffffff;
  }
`;

const SingleGroupHint = styled.div`
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  text-align: center;
`;

const StudentSubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
  margin-top: 8px;
  
  &:hover {
    background: var(--color-accent);
  }
  
  &:disabled {
    background: var(--color-save-button-disabled-bg);
    color: var(--color-save-button-disabled-text);
    cursor: not-allowed;
  }
`;

const StudentErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 8px;
  margin-top: 16px;
  text-align: center;
  font-size: 0.9rem;
`;

const LoginCard = styled.div`
  background: var(--color-surface);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 450px;
  width: 90%;
  position: relative;
  z-index: 10;
  margin-bottom: 200px;
  
  /* 다크모드: 테두리 추가 */
  :root:not([data-theme='light']) & {
    border: 2px solid var(--color-border);
  }
`;

const Title = styled.h1`
  color: var(--color-text-primary);
  margin-bottom: 10px;
  font-size: 2rem;
  font-weight: bold;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 10px;
  font-size: 1rem;
  
  /* 다크모드: 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: #777777;
  }
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`;

const Label = styled.label`
  color: var(--color-text-primary);
  font-weight: bold;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  text-align: center;
  transition: border-color 0.3s ease;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
  
  &::placeholder {
    color: #999;
  }
  
  /* 다크모드: WebKit이 입력 글자색을 덮어쓰는 경우까지 흰색으로 고정 */
  :root:not([data-theme='light']) & {
    background: var(--color-category-input-bg);
    color: #ffffff;
    -webkit-text-fill-color: #ffffff;
    caret-color: #ffffff;
  }
  
  /* 브라우저 자동완성(autofill) 시 배경색을 연한 순수 회색으로 (기본 #E8F0FE 오버라이드) */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px #F0F0F0 inset !important;
    box-shadow: 0 0 0 30px #F0F0F0 inset !important;
    -webkit-text-fill-color: #111111 !important;
  }
  :root:not([data-theme='light']) &:-webkit-autofill,
  :root:not([data-theme='light']) &:-webkit-autofill:hover,
  :root:not([data-theme='light']) &:-webkit-autofill:focus,
  :root:not([data-theme='light']) &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px #2f2f2f inset !important;
    box-shadow: 0 0 0 30px #2f2f2f inset !important;
    -webkit-text-fill-color: #ffffff !important;
    color: #ffffff !important;
  }
`;

const LoginButton = styled.button`
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--color-accent);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    :root:not([data-theme='light']) & {
      background: #4a4a4a;
    }
  }
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-border);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    background: var(--color-panel);
    color: #ccc;
    cursor: not-allowed;
    :root:not([data-theme='light']) & {
      background: #3a3a3a;
      color: #888;
    }
  }
`;

const SocialLoginGroup = styled.div`
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  padding: 20px 0;
  margin: 20px 0;
`;

const RememberMeContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 8px;
  gap: 16px;
`;

const RememberMeCheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const RememberMeCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--color-accent-strong);
  cursor: pointer;
`;

const RememberMeLabel = styled.span`
  color: #666;
  font-size: 0.9rem;
  
  /* 다크모드: 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: #777777;
  }
`;

const BottomLinks = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--color-border);
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: var(--color-accent-text);
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: underline;
  
  &:hover {
    color: var(--color-accent);
  }
  
  &:focus {
    outline: none;
  }
`;



const GoogleIcon = styled.div`
  width: 20px;
  height: 20px;
  background: var(--color-surface);
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: #4285f4;
`;

const ThemeToggleWrapper = styled.div`
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
  padding: 20px 20px 300px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 16px 20px 300px 20px;
  }
`;

const LoginPageActionStack = styled.div`
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
  padding: 20px 20px 300px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 16px 20px 300px 20px;
  }
`;

const PlanPreviewButton = styled.button`
  border: 1px solid var(--color-accent-strong);
  background: rgba(var(--color-accent-rgb), 0.08);
  color: white;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.14);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ThemeToggleButton = styled.button<{ $isDark: boolean }>`
  display: flex;
  background: var(--color-accent-strong);
  border-radius: 13px;
  padding: 2px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 26px;
  min-width: 80px;
  min-height: 26px;
  box-sizing: border-box;
  flex-shrink: 0;
  width: auto;
  border: none;
  appearance: none;
  -webkit-appearance: none;
  
  /* 라이트 모드: 배경을 흰색으로 */
  :root[data-theme='light'] & {
    background: #ffffff;
    border: 1px solid var(--color-border);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isDark ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: var(--color-surface);
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }
  
  /* 라이트 모드: 슬라이더 도형을 메인테마 초록색으로, 위치는 동일하게 유지 */
  :root[data-theme='light'] &::before {
    background: var(--color-accent-strong);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    height: 24px;
    min-width: 72px;
    min-height: 24px;
    border-radius: 12px;
    
    &::before {
      border-radius: 10px;
    }
  }
  
  /* 모바일에서 브라우저 기본 크기로 변경되지 않도록 최소값 설정 */
  @media (max-width: 480px) {
    min-width: 72px;
    height: 24px;
    min-height: 24px;
  }
`;

const ThemeToggleOption = styled.div<{ $isActive: boolean; $isDark: boolean }>`
  flex: 1;
  padding: 2px 10px;
  font-size: 11px !important;
  font-weight: 600;
  color: ${props => props.$isActive ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)'};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  
  /* 라이트 모드: 활성 옵션은 흰색 (슬라이더 위에 있으므로), 비활성은 반투명 초록색 */
  :root[data-theme='light'] & {
    color: ${props => props.$isActive ? '#ffffff' : 'rgba(var(--color-accent-rgb), 0.6)'};
  }
  
  @media (max-width: 768px) {
    padding: 2px 8px;
    font-size: 10px !important;
  }
  
  /* 모바일에서 브라우저 기본 크기로 변경되지 않도록 최소값 설정 */
  @media (max-width: 480px) {
    font-size: 10px !important;
    padding: 2px 8px;
  }
`;

const LoadingSpinner = styled.div`
  border: 2px solid #f3f3f3;
  border-top: 2px solid #4285f4;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: var(--color-text-primary);
  font-size: 0.9rem;
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
`;

const CompanyInfoSection = styled.div`
  background: var(--color-panel);
  padding: 16px 20px;
  margin-top: auto;
  width: 100%;
  box-sizing: border-box;
  
  /* 다크모드에서 배경색 조정 */
  :root:not([data-theme='light']) & {
    background: rgba(0, 0, 0, 0.2);
  }
  
  /* 라이트모드에서 배경색: #10504A보다 조금 어두운 색상 */
  :root[data-theme='light'] & {
    background: #0d453f;
  }
`;

const CompanyInfoContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;
`;

const CompanyLogo = styled.div`
  flex-shrink: 0;
  
  img {
    height: 20px;
    width: auto;
    opacity: 0.8;
  }
  
  @media (max-width: 768px) {
    img {
      height: 18px;
    }
  }
`;

const CompanyInfoText = styled.div`
  font-size: 11px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  opacity: 0.7;
  text-align: center;
  width: 100%;
  
  /* 라이트 모드에서 텍스트를 흰색으로 */
  :root[data-theme='light'] & {
    color: rgba(255, 255, 255, 0.9);
    opacity: 1;
  }
  
  div {
    margin-bottom: 2px;
  }
  
  div:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const PolicyLinks = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 6px 0;
  
  @media (max-width: 768px) {
    gap: 6px;
    margin: 4px 0;
  }
`;

const PolicyLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: var(--color-text-secondary);
  text-decoration: underline;
  opacity: 0.7;
  font-size: 11px;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
    color: var(--color-accent-strong);
  }
  
  /* 라이트 모드에서 텍스트를 흰색으로 */
  :root[data-theme='light'] & {
    color: rgba(255, 255, 255, 0.9);
    opacity: 1;
    
    &:hover {
      color: rgba(255, 255, 255, 1);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
  
  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const LoginPage: React.FC<LoginPageProps> = ({ onLogin, themeMode = 'dark', onToggleTheme }) => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<'none' | 'student' | 'teacher'>('none');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailAuth, setShowEmailAuth] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => isRememberLoginEnabled());
  const [rememberEmailChecked, setRememberEmailChecked] = useState(() => {
    return localStorage.getItem('remember-email-checked') === 'true';
  });
  
  // 멤버/게스트 입장 폼 상태
  const [activeStudentTab, setActiveStudentTab] = useState<'member' | 'guest'>('member');
  const [memberFormData, setMemberFormData] = useState({
    classCode: '',
    classEntryCode: '',
    studentNumber: '',
    studentName: '',
    accessCode: ''
  });
  const [guestFormData, setGuestFormData] = useState({
    classCode: '',
    classEntryCode: '',
    guestName: ''
  });
  const [studentLoading, setStudentLoading] = useState(false);
  const [studentError, setStudentError] = useState('');
  const [memberClassVerified, setMemberClassVerified] = useState(false);
  const [guestClassVerified, setGuestClassVerified] = useState(false);
  const [verifiedMemberClassName, setVerifiedMemberClassName] = useState('');
  const [verifiedGuestClassName, setVerifiedGuestClassName] = useState('');
  const [memberOrderedGroups, setMemberOrderedGroups] = useState<string[]>(['A']);
  const [memberGroupNameMap, setMemberGroupNameMap] = useState<{ [k: string]: string }>({});
  const [selectedMemberGroup, setSelectedMemberGroup] = useState('');
  const [isConfirmingMemberCode, setIsConfirmingMemberCode] = useState(false);
  const [isConfirmingGuestCode, setIsConfirmingGuestCode] = useState(false);
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyModalType, setPolicyModalType] = useState<'terms' | 'privacy'>('terms');
  const [showPublicLicensePlanModal, setShowPublicLicensePlanModal] = useState(false);
  type StudentAccessResponse = Awaited<ReturnType<typeof classCodeService.validateStudentAccess>>;

  useEffect(() => {
    // 저장된 사용자 정보가 있으면 자동 로그인
    const storedUser = getStoredUser();
    if (storedUser) {
      onLogin(storedUser);
      return;
    }

    // Google API 초기화
    initializeGoogleAuth();
  }, [onLogin]);

  // 저장된 이메일 불러오기 (컴포넌트 마운트 시 한 번만)
  useEffect(() => {
    const savedEmail = localStorage.getItem('remembered-email');
    const isChecked = localStorage.getItem('remember-email-checked') === 'true';
    if (isChecked && savedEmail) {
      setEmail(savedEmail);
      setRememberEmailChecked(true);
    }
  }, []);

  const resetMemberClassVerification = () => {
    setMemberClassVerified(false);
    setVerifiedMemberClassName('');
    setMemberOrderedGroups(['A']);
    setMemberGroupNameMap({});
    setSelectedMemberGroup('');
  };

  const resetGuestClassVerification = () => {
    setGuestClassVerified(false);
    setVerifiedGuestClassName('');
  };

  const handleConfirmMemberClassCode = async () => {
    if (memberClassVerified) return;
    if (memberFormData.classCode.length !== 8 || memberFormData.classEntryCode.trim().length !== 4) return;
    setIsConfirmingMemberCode(true);
    setStudentError('');
    try {
      const meta = await classCodeService.getClassEntryMeta(
        memberFormData.classCode,
        memberFormData.classEntryCode.trim()
      );
      if (!meta.success) {
        resetMemberClassVerification();
        setStudentError(meta.error || '클래스 코드를 확인할 수 없습니다.');
        return;
      }
      const ordered = getOrderedMemberGroups(meta.memberGroups, meta.memberGroupOrder);
      setVerifiedMemberClassName(meta.className?.trim() || '클래스');
      setMemberOrderedGroups(ordered);
      setMemberGroupNameMap(meta.memberGroupNames || {});
      setMemberClassVerified(true);
      if (ordered.length === 1) {
        setSelectedMemberGroup(ordered[0]);
      } else {
        setSelectedMemberGroup('');
      }
    } catch {
      resetMemberClassVerification();
      setStudentError('클래스 코드를 확인할 수 없습니다.');
    } finally {
      setIsConfirmingMemberCode(false);
    }
  };

  const handleConfirmGuestClassCode = async () => {
    if (guestClassVerified) return;
    if (guestFormData.classCode.length !== 8 || guestFormData.classEntryCode.trim().length !== 4) return;
    setIsConfirmingGuestCode(true);
    setStudentError('');
    try {
      const meta = await classCodeService.getClassEntryMeta(
        guestFormData.classCode,
        guestFormData.classEntryCode.trim()
      );
      if (!meta.success) {
        resetGuestClassVerification();
        setStudentError(meta.error || '클래스 코드를 확인할 수 없습니다.');
        return;
      }
      setVerifiedGuestClassName(meta.className?.trim() || '클래스');
      setGuestClassVerified(true);
    } catch {
      resetGuestClassVerification();
      setStudentError('클래스 코드를 확인할 수 없습니다.');
    } finally {
      setIsConfirmingGuestCode(false);
    }
  };

  const memberFieldsEnabled =
    memberClassVerified && selectedMemberGroup !== '' && !isConfirmingMemberCode;

  const handleMemberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let nextValue = value;

    if (name === 'classCode') {
      nextValue = value.replace(/\D/g, '').slice(0, 8);
      resetMemberClassVerification();
    } else if (name === 'classEntryCode') {
      nextValue = value.replace(/\D/g, '').slice(0, 4);
      resetMemberClassVerification();
    }

    setMemberFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  const handleGuestInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'classCode') {
      const numericValue = value.replace(/\D/g, '').slice(0, 8);
      resetGuestClassVerification();
      setGuestFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else if (name === 'classEntryCode') {
      const numericValue = value.replace(/\D/g, '').slice(0, 4);
      resetGuestClassVerification();
      setGuestFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      setGuestFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // 입장하기 버튼 활성화 여부 계산
  const isStudentSubmitButtonDisabled = () => {
    if (studentLoading || isConfirmingMemberCode || isConfirmingGuestCode) {
      return true;
    }

    if (activeStudentTab === 'member') {
      if (!memberClassVerified || selectedMemberGroup === '') {
        return true;
      }
      const hasRequiredInputs =
        memberFormData.classCode.length === 8 &&
        memberFormData.studentNumber.trim() !== '' &&
        memberFormData.studentName.trim() !== '' &&
        memberFormData.accessCode.trim() !== '';
      if (!hasRequiredInputs) {
        return true;
      }
      return false;
    }

    if (!guestClassVerified) {
      return true;
    }
    const guestRequired =
      guestFormData.classCode.length === 8 &&
      guestFormData.classEntryCode.trim().length === 4 &&
      guestFormData.guestName.trim() !== '';
    if (!guestRequired) {
      return true;
    }

    return false;
  };

  const handleStudentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (activeStudentTab === 'member') {
      if (!memberClassVerified || selectedMemberGroup === '') {
        return;
      }
    }
    if (activeStudentTab === 'guest' && !guestClassVerified) {
      return;
    }
    setStudentLoading(true);
    setStudentError('');

    try {
      const isMemberTab = activeStudentTab === 'member';
      let response: StudentAccessResponse;
      if (isMemberTab) {
        if (memberFormData.accessCode.trim() === '') {
          setStudentError('개별 접근 코드를 입력해주세요.');
          setStudentLoading(false);
          return;
        }
        response = await classCodeService.validateStudentAccess(
          memberFormData.classCode,
          memberFormData.studentNumber,
          memberFormData.studentName,
          memberFormData.accessCode.trim(),
          memberFormData.classEntryCode.trim(),
          selectedMemberGroup,
          { deferStudentSummary: true }
        );
      } else {
        response = await classCodeService.validateStudentAccess(
          guestFormData.classCode,
          '',
          guestFormData.guestName,
          undefined,
          guestFormData.classEntryCode
        );
      }

      if (response.success) {
        // 코디 정보 확인 (멤버 페이지 접근 권한 체크)
        const classCode = isMemberTab ? memberFormData.classCode : guestFormData.classCode;
        const codiInfo = await classCodeService.getCodiInfoByClassCode(classCode);
        if (codiInfo?.userType) {
          const accessCheck = checkMemberPageAccess(codiInfo.userType);
          if (!accessCheck.allowed) {
            setLicenseLimitMessage(accessCheck.message || '');
            setShowLicenseLimitModal(true);
            setStudentLoading(false);
            return;
          }
        }

        if (response.isGuestMode) {
          // 게스트 모드인 경우 안내 후 입장
          const guestName = guestFormData.guestName.trim();
          const guestId = `guest-${guestFormData.classCode}-guest-${Date.now().toString(36)}`;
          
          const guestStudentSummary = {
            basicInfo: {
              id: guestId,
              number: '',
              name: guestName || '게스트',
              profileImage: 'basic-0',
              coinsHistory: [],
            },
            totalPoints: 0,
            assignmentPoints: 0,
            positivePoints: 0,
            behaviorPoints: 0,
            profileImage: 'basic-1',
          };
          
          navigate('/member-page', { 
            state: {
              ...response,
              guestName,
              guestNumber: '',
              guestId,
              student: guestStudentSummary,
            }
          });
        } else {
          // 정규 멤버으로 바로 입장
          navigate('/member-page', { state: response });
        }
      } else {
        setStudentError(
          isMemberTab
            ? (response.error || '입력한 정보가 불일치합니다. 정확한 정보를 확인해주세요.')
            : (response.error || '입장에 실패했습니다.')
        );
      }
    } catch (err: any) {
      console.error('멤버 입장 실패:', err);
      setStudentError('입력한 정보가 불일치합니다. 정확한 정보를 확인해주세요.');
    } finally {
      setStudentLoading(false);
    }
  };

  const isStudentButtonDisabled = isStudentSubmitButtonDisabled();

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setError('');
    
    try {
      const user = await signInWithGoogle();
      storeUser(user, rememberMe);
      onLogin(user);
    } catch (err: any) {
      console.error('Login error:', err);
      let errorMessage = 'Google 로그인에 실패했습니다.';
      
      // 상세한 에러 메시지 표시
      if (err.message) {
        errorMessage = err.message;
      } else if (err.code) {
        // Firebase 에러 코드 기반 메시지
        if (err.code === 'auth/popup-closed-by-user') {
          errorMessage = '로그인 창이 닫혔습니다. 다시 시도해주세요.';
        } else if (err.code === 'auth/popup-blocked') {
          errorMessage = '팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.';
        } else if (err.code === 'auth/network-request-failed') {
          errorMessage = '네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.';
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsGoogleLoading(false);
    }
  };


  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('이메일과 비밀번호를 입력해주세요.');
      return;
    }

    if (!email.includes('@')) {
      setError('올바른 이메일 주소를 입력해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      const userCredential = await authService.signIn(email, password);
      const firebaseUser = userCredential;
      
      let userProfile = null;
      try {
        userProfile = await alarmService.getUserProfile(firebaseUser.uid);
      } catch (error) {
        console.error('프로필 가져오기 실패:', error);
      }

      const appUser: User = {
        id: firebaseUser.uid,
        email: firebaseUser.email || email,
        name: userProfile?.name || firebaseUser.displayName || email.split('@')[0],

        userType: userProfile?.userType || 'free',
        picture:
          firebaseUser.photoURL ||
          getUiAvatarPlaceholderUrl(getAvatarInitialsFromEmail(firebaseUser.email || email), {
            size: 40
          }),

        currentClassId: undefined
      };

      // 아이디 기억하기 처리
      if (rememberEmailChecked) {
        localStorage.setItem('remembered-email', email);
        localStorage.setItem('remember-email-checked', 'true');
      } else {
        localStorage.removeItem('remembered-email');
        localStorage.setItem('remember-email-checked', 'false');
      }
      
      storeUser(appUser, rememberMe);
      setTimeout(() => { onLogin(appUser); }, 1000);
    } catch (error: any) {
      console.error('Firebase 로그인 에러:', error);
      let errorMessage = '로그인에 실패했습니다.';
      if (error.code === 'auth/user-not-found') {
        errorMessage = '등록되지 않은 이메일입니다.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = '비밀번호가 올바르지 않습니다.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '올바른 이메일 주소를 입력해주세요.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = '너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도해주세요.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = '비활성화된 계정입니다.';
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAuthSuccess = (user: User) => {
    storeUser(user, rememberMe);
    onLogin(user);
  };

  return (
    <>
    <LoginContainer>
      {selectedRole === 'none' && (
        <>
          <LogoContainer>
            <LogoSmallAbove src="/EDUKODI_LOGO.svg" alt="" aria-hidden />
            <LogoImage src="/LOGOeducodi_new.svg" alt="에듀코디 로고" />
          </LogoContainer>
          
          <RoleSelectionContainer>
            <RoleCard onClick={() => setSelectedRole('student')}>
              <RoleIcon>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </RoleIcon>
              <RoleTitle>멤버</RoleTitle>
              <RoleDescription>클래스에 입장하기</RoleDescription>
            </RoleCard>
            
            <RoleCard onClick={() => setSelectedRole('teacher')}>
              <RoleIcon>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 14L21 9L12 4L3 9L12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 9V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 11.5V16.5C6 16.5 8 19 12 19C16 19 18 16.5 18 16.5V11.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </RoleIcon>
              <RoleTitle>코디</RoleTitle>
              <RoleDescription>클래스 관리하기</RoleDescription>
            </RoleCard>
          </RoleSelectionContainer>
          
          <LoginPageActionStack>
            {/* 테마 토글 버튼 - 멤버/코디 선택 화면에서만 표시 */}
            {onToggleTheme && (
              <ThemeToggleWrapper style={{ padding: 0 }}>
                <ThemeToggleButton
                  $isDark={themeMode === 'dark'}
                  onClick={onToggleTheme}
                  aria-label="테마 전환"
                >
                  <ThemeToggleOption $isActive={themeMode === 'light'} $isDark={themeMode === 'dark'}>
                    일반
                  </ThemeToggleOption>
                  <ThemeToggleOption $isActive={themeMode === 'dark'} $isDark={themeMode === 'dark'}>
                    다크
                  </ThemeToggleOption>
                </ThemeToggleButton>
              </ThemeToggleWrapper>
            )}
            <PlanPreviewButton onClick={() => setShowPublicLicensePlanModal(true)}>
              [이용권 플랜]
            </PlanPreviewButton>
          </LoginPageActionStack>
        </>
      )}
      
      {selectedRole === 'student' && (
        <LoginCard>
          <BackToRoleButton onClick={() => {
            setSelectedRole('none');
            setStudentError('');
            resetMemberClassVerification();
            resetGuestClassVerification();
            setMemberFormData({
              classCode: '',
              classEntryCode: '',
              studentNumber: '',
              studentName: '',
              accessCode: ''
            });
            setGuestFormData({
              classCode: '',
              classEntryCode: '',
              guestName: ''
            });
          }}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            돌아가기
          </BackToRoleButton>
          
          <Title style={{ fontSize: '1.5rem', marginBottom: '8px' }}>클래스 입장</Title>
          <Subtitle style={{ marginBottom: '24px' }}>입장 유형을 선택해주세요</Subtitle>
          
          <form onSubmit={handleStudentSubmit}>
            <StudentTabContainer>
              <StudentTabButton
                type="button"
                $active={activeStudentTab === 'member'}
                onClick={() => {
                  setActiveStudentTab('member');
                  setStudentError('');
                }}
              >
                클래스 멤버 입장
              </StudentTabButton>
              <StudentTabButton
                type="button"
                $active={activeStudentTab === 'guest'}
                onClick={() => {
                  setActiveStudentTab('guest');
                  setStudentError('');
                }}
              >
                클래스 게스트 입장
              </StudentTabButton>
            </StudentTabContainer>

            {activeStudentTab === 'member' ? (
              <>
                <StudentFormGroup>
                  <StudentFormLabel>클래스 코드 (8자리 숫자)</StudentFormLabel>
                  <StudentFormInput
                    type="text"
                    name="classCode"
                    placeholder="8자리 숫자"
                    value={memberFormData.classCode}
                    onChange={handleMemberInputChange}
                    maxLength={8}
                    inputMode="numeric"
                    autoComplete="off"
                    required
                  />
                  <StudentFormLabel style={{ marginTop: 14 }}>클래스 입장 코드</StudentFormLabel>
                  <StudentFormInput
                    type="text"
                    name="classEntryCode"
                    placeholder="4자리 숫자"
                    value={memberFormData.classEntryCode}
                    onChange={handleMemberInputChange}
                    maxLength={4}
                    inputMode="numeric"
                    autoComplete="off"
                  />
                  <MemberClassVerifyButton
                    type="button"
                    onClick={handleConfirmMemberClassCode}
                    disabled={
                      memberClassVerified ||
                      memberFormData.classCode.length !== 8 ||
                      memberFormData.classEntryCode.trim().length !== 4 ||
                      isConfirmingMemberCode
                    }
                  >
                    {memberClassVerified
                      ? '클래스 정보 확인 완료'
                      : isConfirmingMemberCode
                        ? '확인 중…'
                        : '확인'}
                  </MemberClassVerifyButton>
                </StudentFormGroup>

                {memberClassVerified && (
                  <>
                    <StudentFormGroup>
                      <StudentFormLabel>클래스 이름 · 그룹</StudentFormLabel>
                      <VerifiedClassMeta>
                        <VerifiedClassName>{verifiedMemberClassName}</VerifiedClassName>
                        {memberOrderedGroups.length > 1 ? (
                          <GroupSelect
                            value={selectedMemberGroup}
                            onChange={(e) => setSelectedMemberGroup(e.target.value)}
                            aria-label="멤버 그룹 선택"
                          >
                            <option value="">그룹 선택</option>
                            {memberOrderedGroups.map((key) => (
                              <option key={key} value={key}>
                                {memberGroupNameMap[key]?.trim() || `${key} 그룹`}
                              </option>
                            ))}
                          </GroupSelect>
                        ) : (
                          <SingleGroupHint>
                            그룹:{' '}
                            {memberGroupNameMap[memberOrderedGroups[0]]?.trim() ||
                              `${memberOrderedGroups[0]} 그룹`}
                          </SingleGroupHint>
                        )}
                      </VerifiedClassMeta>
                    </StudentFormGroup>

                    <StudentFormGroup>
                      <StudentFormLabel>멤버 번호</StudentFormLabel>
                      <StudentFormInput
                        type="text"
                        name="studentNumber"
                        placeholder="번호 입력"
                        value={memberFormData.studentNumber}
                        onChange={handleMemberInputChange}
                        disabled={!memberFieldsEnabled}
                        required
                      />
                    </StudentFormGroup>

                    <StudentFormGroup>
                      <StudentFormLabel>멤버 이름</StudentFormLabel>
                      <StudentFormInput
                        type="text"
                        name="studentName"
                        placeholder="이름 입력"
                        value={memberFormData.studentName}
                        onChange={handleMemberInputChange}
                        disabled={!memberFieldsEnabled}
                        required
                      />
                    </StudentFormGroup>

                    <StudentFormGroup>
                      <StudentFormLabel>개별 접근 코드</StudentFormLabel>
                      <StudentFormInput
                        type="text"
                        name="accessCode"
                        placeholder={
                          !memberFieldsEnabled ? '그룹 선택 후 입력하세요' : '개별 접근 코드 입력'
                        }
                        value={memberFormData.accessCode}
                        onChange={handleMemberInputChange}
                        disabled={!memberFieldsEnabled}
                        autoComplete="off"
                        required
                      />
                    </StudentFormGroup>

                    <StudentSubmitButton type="submit" disabled={isStudentButtonDisabled}>
                      {studentLoading ? '입장 중...' : '클래스 입장하기'}
                    </StudentSubmitButton>
                  </>
                )}
              </>
            ) : (
              <>
                <StudentFormGroup>
                  <StudentFormLabel>클래스 코드 (8자리 숫자)</StudentFormLabel>
                  <StudentFormInput
                    type="text"
                    name="classCode"
                    placeholder="8자리 숫자"
                    value={guestFormData.classCode}
                    onChange={handleGuestInputChange}
                    maxLength={8}
                    inputMode="numeric"
                    autoComplete="off"
                    required
                  />
                  <StudentFormLabel style={{ marginTop: 14 }}>클래스 입장 코드</StudentFormLabel>
                  <StudentFormInput
                    type="text"
                    name="classEntryCode"
                    placeholder="4자리 숫자"
                    value={guestFormData.classEntryCode}
                    onChange={handleGuestInputChange}
                    maxLength={4}
                    inputMode="numeric"
                    autoComplete="off"
                  />
                  <MemberClassVerifyButton
                    type="button"
                    onClick={handleConfirmGuestClassCode}
                    disabled={
                      guestClassVerified ||
                      guestFormData.classCode.length !== 8 ||
                      guestFormData.classEntryCode.trim().length !== 4 ||
                      isConfirmingGuestCode
                    }
                  >
                    {guestClassVerified
                      ? '클래스 정보 확인 완료'
                      : isConfirmingGuestCode
                        ? '확인 중…'
                        : '확인'}
                  </MemberClassVerifyButton>
                </StudentFormGroup>

                {guestClassVerified && (
                  <>
                    <StudentFormGroup>
                      <StudentFormLabel>클래스 이름</StudentFormLabel>
                      <VerifiedClassMeta>
                        <VerifiedClassName>{verifiedGuestClassName}</VerifiedClassName>
                      </VerifiedClassMeta>
                    </StudentFormGroup>

                    <StudentFormGroup>
                      <StudentFormLabel>게스트 이름</StudentFormLabel>
                      <StudentFormInput
                        type="text"
                        name="guestName"
                        placeholder="이름 입력"
                        value={guestFormData.guestName}
                        onChange={handleGuestInputChange}
                        required
                      />
                    </StudentFormGroup>

                    <StudentSubmitButton type="submit" disabled={isStudentButtonDisabled}>
                      {studentLoading ? '입장 중...' : '클래스 게스트 입장하기'}
                    </StudentSubmitButton>
                  </>
                )}
              </>
            )}
          </form>
          
          {studentError && <StudentErrorMessage>{studentError}</StudentErrorMessage>}
        </LoginCard>
      )}
      
      {selectedRole === 'teacher' && (
        <LoginCard>
          <BackToRoleButton onClick={() => {
            setSelectedRole('none');
            setError('');
          }}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            돌아가기
          </BackToRoleButton>
          
          <Title style={{ fontSize: '1.5rem', marginBottom: '8px' }}>코디 로그인</Title>
          <Subtitle style={{ marginBottom: '24px' }}>에듀코디에 오신걸 환영합니다!</Subtitle>
          
          <LoginForm onSubmit={handleEmailLogin}>
            <InputGroup>
              <Label>이메일</Label>
              <Input
                type="email"
                placeholder="example@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </InputGroup>
            
            <InputGroup>
              <Label>비밀번호</Label>
              <Input
                type="password"
                placeholder="비밀번호 입력"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </InputGroup>
            
            <LoginButton type="submit" disabled={isLoading}>
              {isLoading ? '로그인 중...' : '로그인'}
            </LoginButton>
          </LoginForm>
          
          <RememberMeContainer>
            <RememberMeCheckboxContainer>
              <RememberMeCheckbox
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <RememberMeLabel>로그인 상태 유지</RememberMeLabel>
            </RememberMeCheckboxContainer>
            <RememberMeCheckboxContainer>
              <RememberMeCheckbox
                type="checkbox"
                checked={rememberEmailChecked}
                onChange={(e) => setRememberEmailChecked(e.target.checked)}
              />
              <RememberMeLabel>아이디 기억하기</RememberMeLabel>
            </RememberMeCheckboxContainer>
          </RememberMeContainer>
          
          <SocialLoginGroup>
            <GoogleButton 
              onClick={handleGoogleSignIn} 
              disabled={isGoogleLoading}
            >
              {isGoogleLoading ? (
                <LoadingSpinner />
              ) : (
                <GoogleIcon>G</GoogleIcon>
              )}
              {isGoogleLoading ? '로그인 중...' : 'Google로 로그인'}
            </GoogleButton>
          </SocialLoginGroup>
          
          <BottomLinks>
            <LinkButton onClick={() => setShowPasswordReset(true)}>
              계정/비밀번호 찾기
            </LinkButton>
            <LinkButton onClick={() => setShowEmailAuth(true)}>
              회원가입
            </LinkButton>
          </BottomLinks>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </LoginCard>
      )}
      
      {/* 회사 정보 섹션 */}
      <CompanyInfoSection>
        <CompanyInfoContent>
          <CompanyLogo>
            <img src="/LOGOeducodi_new.svg" alt="에듀코디" />
          </CompanyLogo>
          <CompanyInfoText>
            <div>제이앤조이 대표자 : 정영은</div>
            <div>통신판매업신고번호 : 2024-화성동탄-0316호</div>
            <div>사업자등록번호 : 702-04-02761</div>
            <div>주소 : 경기도 화성시 동탄대로시범길 20. 1415동 2701호</div>
            <PolicyLinks>
              <PolicyLink onClick={() => {
                setPolicyModalType('terms');
                setShowPolicyModal(true);
              }}>[서비스 이용약관]</PolicyLink>
              <PolicyLink onClick={() => {
                setPolicyModalType('privacy');
                setShowPolicyModal(true);
              }}>[개인정보처리방침]</PolicyLink>
            </PolicyLinks>
            <div>연락처 : 010-7442-0199</div>
            <div>결제 및 상담 : luckytem_mk@naver.com</div>
          </CompanyInfoText>
        </CompanyInfoContent>
      </CompanyInfoSection>
    </LoginContainer>
      
    <LicenseLimitModal
      isOpen={showLicenseLimitModal}
      message={licenseLimitMessage}
      onClose={() => setShowLicenseLimitModal(false)}
      showUpgradeButton={false}
    />
    
    <PolicyViewModal
      isOpen={showPolicyModal}
      onClose={() => setShowPolicyModal(false)}
      type={policyModalType}
    />

    <PublicLicensePlanModal
      isOpen={showPublicLicensePlanModal}
      onClose={() => setShowPublicLicensePlanModal(false)}
    />
    
      
    <EmailAuthModal
      isOpen={showEmailAuth}
      onClose={() => setShowEmailAuth(false)}
      onSuccess={handleEmailAuthSuccess}
    />
    
    <IdPasswordFindModal
      isOpen={showPasswordReset}
      onClose={() => setShowPasswordReset(false)}
    />
    </>
  );
};

export default LoginPage; 